import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import {
  CheckCircle2, ClipboardList, Loader2, LogOut, MapPinned, QrCode, ScanLine, X, AlertTriangle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import InterviewSignature from "@/components/InterviewSignature";
import { DocPreview, docFileUrl } from "@/components/DocPreview";

function collectItems(form) {
  const items = [];
  (form?.sections || []).forEach((section) => {
    if (section.groups) {
      section.groups.forEach((group) => (group.items || []).forEach((it) => items.push(it)));
    } else {
      (section.items || []).forEach((it) => items.push(it));
    }
  });
  return items;
}

function computeTotals(form, scores) {
  let total = 0;
  const breakdown = {};
  const mean = (items) => {
    const vals = (items || []).map((it) => Number(scores[it.key]) || 0);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  };
  (form?.sections || []).forEach((section) => {
    if (section.groups) {
      let sec = 0;
      section.groups.forEach((group) => {
        const gs = Math.round(mean(group.items) * (Number(group.weight) || 0) / 100 * 100) / 100;
        breakdown[group.id] = gs;
        sec += gs;
      });
      breakdown[section.id] = Math.round(sec * 100) / 100;
      total += sec;
    } else {
      const ss = Math.round(mean(section.items) * (Number(section.weight) || 0) / 100 * 100) / 100;
      breakdown[section.id] = ss;
      total += ss;
    }
  });
  return { total: Math.round(total * 100) / 100, breakdown };
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">{label}</p>
      <div className="mt-0.5 text-sm font-semibold text-[#1F2937]">{children}</div>
    </div>
  );
}

function rupiah(value) {
  const n = Number(String(value ?? "").replace(/[^\d]/g, ""));
  if (!value || Number.isNaN(n) || n === 0) return "Rp 0";
  return `Rp ${n.toLocaleString("id-ID")}`;
}

function toNumber(value) {
  const n = Number(String(value ?? "").replace(/[^\d]/g, ""));
  return Number.isNaN(n) ? 0 : n;
}

function RefRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 py-0.5">
      <span className="text-xs text-[#6B7280]">{label}</span>
      <span className="text-right text-xs font-bold text-[#1F2937]">{value || "-"}</span>
    </div>
  );
}

function RefBox({ children }) {
  return (
    <div className="mt-2 rounded-lg border border-[#D3EFDF] bg-[#F0FBF5] p-3" data-testid="economy-reference">
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-[#0B6B3A]">Data dari pendaftaran mahasiswa</p>
      {children}
    </div>
  );
}

function EconomyReference({ itemKey, candidate, onPreview }) {
  const family = candidate?.family || {};
  const docs = candidate?.reference_documents || [];
  const photoFront = docs.find((d) => d.doc_type === "Foto Tampak Depan Rumah");
  const photoRoom = docs.find((d) => d.doc_type === "Foto Ruang Tamu / Ruang Keluarga");
  const others = family.otherMembers || [];

  if (itemKey === "eko_1") {
    const totalIncome =
      toNumber(family.father?.income) +
      toNumber(family.mother?.income) +
      others.reduce((sum, m) => sum + toNumber(m.income), 0);
    const dependents = Number(family.otherMemberCount) || others.length || 0;
    return (
      <RefBox>
        <RefRow label="Penghasilan Ayah" value={rupiah(family.father?.income)} />
        <RefRow label="Penghasilan Ibu" value={rupiah(family.mother?.income)} />
        <RefRow label="Penghasilan Anggota Lain" value={rupiah(others.reduce((s, m) => s + toNumber(m.income), 0))} />
        <div className="my-1.5 border-t border-[#D3EFDF]" />
        <RefRow label="Total Penghasilan Keluarga" value={rupiah(totalIncome)} />
        <RefRow label="Jumlah Tanggungan" value={`${dependents} orang`} />
        <DependentsTable candidate={candidate} />
      </RefBox>
    );
  }

  if (itemKey === "eko_2") {
    return (
      <RefBox>
        <RefRow label="Status Kepemilikan Rumah" value={family.homeOwnership} />
        <RefRow label="Kondisi Fisik Rumah" value={family.homeCondition} />
        {(photoFront || photoRoom) && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            {[photoFront, photoRoom].filter(Boolean).map((doc) => (
              <button
                key={doc.id}
                type="button"
                onClick={() => onPreview(doc)}
                data-testid={`economy-photo-${doc.id}`}
                className="overflow-hidden rounded-lg border border-gray-200 bg-white text-left hover:shadow-md"
              >
                <img src={docFileUrl(doc)} alt={doc.doc_type} className="h-20 w-full object-cover" />
                <p className="truncate px-2 py-1 text-[10px] font-semibold text-[#1F2937]">{doc.doc_type}</p>
              </button>
            ))}
          </div>
        )}
      </RefBox>
    );
  }

  if (itemKey === "eko_3") {
    return (
      <RefBox>
        <RefRow label="Pekerjaan Ayah" value={`${family.father?.job || "-"} (${family.father?.status || "-"})`} />
        <RefRow label="Pekerjaan Ibu" value={`${family.mother?.job || "-"} (${family.mother?.status || "-"})`} />
      </RefBox>
    );
  }

  if (itemKey === "eko_4") {
    return (
      <RefBox>
        <RefRow label="Estimasi Penghasilan / Bulan" value={family.incomeRange} />
        <RefRow label="Daya Listrik Rumah" value={family.electricityPower} />
      </RefBox>
    );
  }

  if (itemKey === "eko_5") {
    return (
      <RefBox>
        <p className="text-xs italic text-[#6B7280]">Tidak ada data dari pendaftaran — konfirmasi langsung ke mahasiswa.</p>
      </RefBox>
    );
  }

  return null;
}

function DependentsTable({ candidate }) {
  const others = candidate?.family?.otherMembers || [];
  if (!others.length) return null;
  return (
    <div className="mt-2 overflow-x-auto rounded-lg border border-[#D3EFDF] bg-white p-2" data-testid="dependents-reference">
      <table className="w-full min-w-[420px] text-left text-xs">
        <thead className="text-[#6B7280]">
          <tr>
            <th className="py-1 pr-2 font-semibold">Nama</th>
            <th className="py-1 pr-2 font-semibold">Hubungan</th>
            <th className="py-1 pr-2 font-semibold">Pendidikan</th>
            <th className="py-1 font-semibold">Pekerjaan / Status</th>
          </tr>
        </thead>
        <tbody>
          {others.map((m, i) => (
            <tr key={i} className="border-t border-gray-100 text-[#1F2937]">
              <td className="py-1.5 pr-2 font-semibold">{m.name || "-"}</td>
              <td className="py-1.5 pr-2">{m.relationship || "-"}</td>
              <td className="py-1.5 pr-2">{m.education || "-"}</td>
              <td className="py-1.5">{m.job || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScoreItem({ item, section, value, note, onScore, onNote, reference }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-[#F9FAFB] p-4" data-testid={`score-item-${item.key}`}>
      <div className="flex items-start justify-between gap-3">
        <label htmlFor={`score-${item.key}`} className="text-sm font-semibold text-[#1F2937]">{item.label}</label>
        <div className="flex shrink-0 items-center gap-1">
          <input
            type="number"
            min="1"
            max="100"
            value={value}
            onChange={(e) => {
              const n = Math.max(1, Math.min(100, Number(e.target.value) || 0));
              onScore(item.key, n);
            }}
            data-testid={`score-input-${item.key}`}
            className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-center text-sm font-bold text-[#0B6B3A] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#27AE60]"
          />
          <span className="text-xs font-semibold text-[#9CA3AF]">/100</span>
        </div>
      </div>
      <input
        id={`score-${item.key}`}
        type="range"
        min="1"
        max="100"
        value={value}
        onChange={(e) => onScore(item.key, Number(e.target.value))}
        data-testid={`score-range-${item.key}`}
        className="mt-2 h-2 w-full cursor-pointer appearance-none rounded-full bg-gray-200 accent-[#27AE60]"
      />
      {section.item_notes && (
        <textarea
          rows={2}
          value={note || ""}
          onChange={(e) => onNote(item.key, e.target.value)}
          placeholder="Catatan (opsional)"
          data-testid={`score-note-${item.key}`}
          className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#27AE60]"
        />
      )}
      {reference}
    </div>
  );
}

function ScoreForm({ candidate, form, onSaved }) {
  const allItems = collectItems(form);
  const [scores, setScores] = useState(() => Object.fromEntries(allItems.map((it) => [it.key, 80])));
  const [itemNotes, setItemNotes] = useState({});
  const [decision, setDecision] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [preview, setPreview] = useState(null);

  const { total, breakdown } = computeTotals(form, scores);
  const onScore = (key, val) => setScores((p) => ({ ...p, [key]: val }));
  const onNote = (key, val) => setItemNotes((p) => ({ ...p, [key]: val }));

  const openConfirm = () => {
    if (!notes.trim()) {
      toast.error("Catatan / Alasan Utama Pewawancara wajib diisi.");
      return;
    }
    if (!decision) {
      toast.error("Pilih rekomendasi kelayakan terlebih dahulu.");
      return;
    }
    setConfirmOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/interviewer/scores", {
        cpm_id: candidate.cpm_id,
        scores: Object.fromEntries(allItems.map((it) => [it.key, Number(scores[it.key])])),
        item_notes: itemNotes,
        decision,
        notes,
      });
      toast.success("Penilaian berhasil disimpan.");
      setConfirmOpen(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="interview-score-form">
      {(form?.sections || []).map((section) => (
        <div key={section.id} className="space-y-3" data-testid={`form-section-${section.id}`}>
          <div className="flex items-center justify-between border-b border-gray-100 pb-2">
            <h3 className="font-display text-base font-bold text-[#1F2937]">{section.title}</h3>
            <span className="rounded-full bg-[#E8F6EE] px-2.5 py-1 text-xs font-bold text-[#0B6B3A]">Bobot {section.weight}%</span>
          </div>
          {section.instruction && <p className="text-xs text-[#6B7280]">{section.instruction}</p>}
          {section.groups ? (
            section.groups.map((group) => (
              <div key={group.id} className="space-y-3 rounded-xl border border-gray-100 p-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold text-[#0B6B3A]">{group.title}</h4>
                  <span className="text-xs font-semibold text-[#6B7280]">Bobot {group.weight}% · Subtotal {breakdown[group.id] ?? 0}</span>
                </div>
                {group.items.map((item) => (
                  <ScoreItem key={item.key} item={item} section={section} value={scores[item.key]} note={itemNotes[item.key]} onScore={onScore} onNote={onNote} />
                ))}
              </div>
            ))
          ) : (
            section.items.map((item) => (
              <ScoreItem
                key={item.key}
                item={item}
                section={section}
                value={scores[item.key]}
                note={itemNotes[item.key]}
                onScore={onScore}
                onNote={onNote}
                reference={section.id === "ekonomi" ? (
                  <EconomyReference itemKey={item.key} candidate={candidate} onPreview={setPreview} />
                ) : null}
              />
            ))
          )}
          <div className="flex items-center justify-between rounded-lg bg-[#F0FBF5] px-4 py-2">
            <span className="text-xs font-bold text-[#0B6B3A]">Subtotal {section.title}</span>
            <span className="font-display text-sm font-extrabold text-[#0B6B3A]" data-testid={`subtotal-${section.id}`}>{breakdown[section.id] ?? 0}</span>
          </div>
        </div>
      ))}

      <div className="flex items-center justify-between rounded-xl bg-[#0B6B3A] px-5 py-4 text-white">
        <span className="text-sm font-bold">TOTAL SKOR AKHIR</span>
        <span className="font-display text-2xl font-extrabold" data-testid="interview-score-total">{total}</span>
      </div>

      <div>
        <label htmlFor="interview-notes" className="mb-1.5 block text-sm font-semibold text-[#1F2937]">
          Catatan / Alasan Utama Pewawancara <span className="text-[#DC2626]">*</span>
        </label>
        <textarea
          id="interview-notes"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder='Wajib diisi. Contoh: "Anak ke-3 dari 4 bersaudara, ayah buruh harian, sangat layak dibantu."'
          data-testid="interview-notes"
          className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#27AE60]"
        />
        <p className="mt-1 text-xs text-[#6B7280]">
          Catatan kualitatif ini menjadi bukti audit terpenting bila ada sanggahan di kemudian hari.
        </p>
      </div>

      <div>
        <p className="mb-1.5 text-sm font-semibold text-[#1F2937]">Rekomendasi Kelayakan <span className="text-[#DC2626]">*</span></p>
        <div className="grid grid-cols-1 gap-2">
          {(form?.decisions || []).map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setDecision(option.value)}
              data-testid={`decision-${option.value}`}
              className={[
                "rounded-xl border px-4 py-2.5 text-left text-sm font-bold transition-colors",
                decision === option.value
                  ? "border-[#27AE60] bg-[#E8F6EE] text-[#0B6B3A]"
                  : "border-gray-200 text-[#6B7280] hover:border-[#27AE60]",
              ].join(" ")}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-3 text-xs font-semibold text-[#92400E]" data-testid="interview-save-warning">
        Perhatian: setelah penilaian dikirim, data <strong>tidak dapat diubah lagi</strong>. Pastikan seluruh skor dan catatan sudah benar.
      </div>

      <button
        type="button"
        onClick={openConfirm}
        disabled={saving}
        data-testid="submit-interview-score"
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#27AE60] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#0B6B3A] disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
        Simpan Penilaian
      </button>

      {confirmOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="interview-confirm-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#FEF3C7] text-[#B45309]">
                <AlertTriangle className="h-5 w-5" />
              </span>
              <h3 className="font-display text-lg font-extrabold text-[#1F2937]">Konfirmasi Penilaian</h3>
            </div>
            <p className="mt-4 text-sm text-[#374151]">
              Mohon periksa kembali seluruh skor dan catatan untuk <strong>{candidate.name}</strong>.
            </p>
            <div className="mt-3 flex items-center justify-between rounded-xl bg-[#F0FBF5] px-4 py-3">
              <span className="text-sm font-bold text-[#0B6B3A]">Total Skor Akhir</span>
              <span className="font-display text-xl font-extrabold text-[#0B6B3A]">{total}</span>
            </div>
            <p className="mt-3 rounded-lg bg-[#FFFBEB] px-3 py-2 text-xs font-semibold text-[#92400E]">
              Setelah disimpan, penilaian <strong>TIDAK dapat diedit lagi</strong>. Pastikan data sudah benar.
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">
              <button
                type="button"
                onClick={submit}
                disabled={saving}
                data-testid="confirm-save-interview-score"
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#27AE60] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#0B6B3A] disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Ya, Simpan Penilaian
              </button>
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={saving}
                data-testid="cancel-save-interview-score"
                className="flex-1 rounded-xl border border-gray-300 px-5 py-3 text-sm font-bold text-[#1F2937] transition-colors hover:bg-gray-50 disabled:opacity-60"
              >
                Periksa Lagi
              </button>
            </div>
          </div>
        </div>
      )}

      {preview && <DocPreview doc={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function QrScanner({ onDetected, onClose }) {
  const containerId = "interviewer-qr-reader";
  const scannerRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      const scanner = new Html5Qrcode(containerId, { verbose: false });
      scannerRef.current = scanner;
      let detected = false;
      scanner
        .start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => {
            if (detected) return;
            detected = true;
            onDetected(decodedText);
          },
          () => {},
        )
        .catch(() => {
          if (!cancelled) {
            setError("Tidak dapat mengakses kamera. Izinkan akses kamera atau masukkan CPM-ID secara manual.");
          }
        });
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      const scanner = scannerRef.current;
      scannerRef.current = null;
      if (scanner) {
        try {
          if (scanner.isScanning) {
            scanner.stop().then(() => scanner.clear()).catch(() => {});
          } else {
            scanner.clear();
          }
        } catch {
          /* abaikan error pembersihan kamera */
        }
      }
    };
  }, [onDetected]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="qr-scanner-modal">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h3 className="font-display text-base font-bold text-[#1F2937]">Pindai QR Mahasiswa</h3>
          <button type="button" onClick={onClose} data-testid="close-qr-scanner" className="rounded-lg p-1.5 text-[#6B7280] hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5">
          <div id={containerId} className="overflow-hidden rounded-xl bg-black" />
          {error && <p className="mt-3 text-xs font-semibold text-[#991B1B]" data-testid="qr-scanner-error">{error}</p>}
          <p className="mt-3 text-center text-xs text-[#6B7280]">Arahkan kamera ke QR Code pada Kartu Bukti Pendaftaran mahasiswa.</p>
        </div>
      </div>
    </div>
  );
}


export default function InterviewerDashboard() {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [manualId, setManualId] = useState("");
  const [loadingCandidate, setLoadingCandidate] = useState(false);
  const [candidate, setCandidate] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/interviewer/me");
      setProfile(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const resolveCandidate = useCallback(async (cpmId) => {
    const value = String(cpmId || "").trim();
    if (!value) return;
    setScanning(false);
    setLoadingCandidate(true);
    setCandidate(null);
    try {
      const { data } = await api.get("/interviewer/candidate", { params: { cpm_id: value } });
      setCandidate(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoadingCandidate(false);
    }
  }, []);

  const onSaved = () => {
    loadProfile();
    resolveCandidate(candidate.cpm_id);
  };

  return (
    <main className="min-h-screen bg-[#F6FAF7] text-[#1F2937]" data-testid="interviewer-dashboard">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#27AE60] font-display text-xs font-extrabold text-white">MDJ</span>
            <div className="min-w-0">
              <strong className="block truncate font-display text-sm font-extrabold">Portal Pewawancara</strong>
              <span className="flex items-center gap-1 text-[11px] font-semibold text-[#0B6B3A]">
                <MapPinned className="h-3 w-3" /> {profile?.region || "—"}
              </span>
            </div>
          </div>
          <button type="button" onClick={logout} data-testid="interviewer-logout" className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-[#DC2626] hover:bg-[#FEE2E2]">
            <LogOut className="h-4 w-4" /> Keluar
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <section className="rounded-2xl border border-gray-100 bg-white p-6 shadow-[0_4px_20px_-2px_rgba(39,174,96,0.06)]">
          <p className="text-xs font-bold uppercase tracking-wide text-[#0B6B3A]">Selamat bertugas,</p>
          <h1 className="mt-1 font-display text-2xl font-extrabold text-[#1F2937]" data-testid="interviewer-name">{user?.name || profile?.name}</h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Pindai QR Code pada Kartu Bukti Pendaftaran mahasiswa untuk membuka formulir penilaian wawancara.
            Total dinilai: <strong className="text-[#0B6B3A]" data-testid="interviewer-scored-count">{profile?.scored_count ?? 0}</strong>
          </p>

          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => setScanning(true)}
              data-testid="scan-qr-btn"
              className="flex items-center justify-center gap-2 rounded-xl bg-[#27AE60] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#0B6B3A]"
            >
              <ScanLine className="h-5 w-5" /> Scan QR Mahasiswa
            </button>
            <div className="flex flex-1 gap-2">
              <input
                value={manualId}
                onChange={(e) => setManualId(e.target.value)}
                placeholder="Atau masukkan CPM-ID manual"
                data-testid="manual-cpm-input"
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#27AE60]"
              />
              <button
                type="button"
                onClick={() => resolveCandidate(manualId)}
                data-testid="manual-cpm-submit"
                className="shrink-0 rounded-xl border border-gray-300 px-4 py-3 text-sm font-bold text-[#0B6B3A] hover:bg-[#E8F6EE]"
              >
                Cari
              </button>
            </div>
          </div>
        </section>

        {loadingCandidate && (
          <div className="mt-6 flex justify-center" data-testid="candidate-loading">
            <Loader2 className="h-6 w-6 animate-spin text-[#27AE60]" />
          </div>
        )}

        {candidate && !loadingCandidate && (
          <section className="mt-6 rounded-2xl border border-gray-100 bg-white p-6 shadow-[0_4px_20px_-2px_rgba(39,174,96,0.06)]" data-testid="candidate-panel">
            <div className="flex items-start gap-3 border-b border-gray-100 pb-5">
              {candidate.photo ? (
                <img
                  src={docFileUrl(candidate.photo)}
                  alt={candidate.name}
                  data-testid="candidate-photo"
                  className="h-16 w-14 shrink-0 cursor-zoom-in rounded-lg border border-gray-200 object-cover"
                  onClick={() => setPhotoPreview(candidate.photo)}
                />
              ) : (
                <span className="flex h-16 w-14 shrink-0 items-center justify-center rounded-lg bg-[#E8F6EE] text-[#0B6B3A]"><QrCode className="h-5 w-5" /></span>
              )}
              <div>
                <h2 className="font-display text-xl font-extrabold text-[#1F2937]" data-testid="candidate-name">{candidate.name}</h2>
                <p className="font-mono text-sm font-semibold text-[#0B6B3A]">{candidate.cpm_id}</p>
              </div>
            </div>
            <div className="grid gap-4 py-5 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Perguruan Tinggi">{candidate.campus}</Field>
              <Field label="Jenjang / Prodi">{candidate.jenjang} · {candidate.jurusan}</Field>
              <Field label="NIM">{candidate.nim}</Field>
              <Field label="Wilayah">{candidate.region}</Field>
              <Field label="Kategori">{candidate.category}</Field>
            </div>

            {candidate.already_scored ? (
              <div className="rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] p-5" data-testid="already-scored-panel">
                <p className="flex items-center gap-2 text-sm font-bold text-[#1E40AF]">
                  <ClipboardList className="h-4 w-4" /> Mahasiswa ini sudah dinilai (hanya dapat dinilai satu kali).
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {collectItems(candidate.form || profile?.form).map((item) => (
                    <Field key={item.key} label={item.label}>{candidate.score?.scores?.[item.key] ?? "-"}</Field>
                  ))}
                  <Field label="Total Skor Akhir">{candidate.score?.total}</Field>
                  <Field label="Rekomendasi">
                    {(candidate.form || profile?.form)?.decisions?.find((d) => d.value === candidate.score?.decision)?.label || candidate.score?.decision}
                  </Field>
                </div>
                {candidate.score?.notes && <div className="mt-3"><Field label="Catatan">{candidate.score.notes}</Field></div>}
                <p className="mt-3 text-xs text-[#6B7280]">Dinilai oleh {candidate.score?.interviewer_name}</p>
                <InterviewSignature score={candidate.score} />
              </div>
            ) : (
              <ScoreForm
                candidate={candidate}
                form={candidate.form || profile?.form}
                onSaved={onSaved}
              />
            )}
          </section>
        )}
      </div>

      {scanning && <QrScanner onDetected={resolveCandidate} onClose={() => setScanning(false)} />}
      {photoPreview && <DocPreview doc={photoPreview} onClose={() => setPhotoPreview(null)} />}
    </main>
  );
}
